import type { Stripe, StripeElements, StripeElementsOptions, StripeError } from '@stripe/stripe-js'
import { getStripeLocale } from '@elo-kit/utils/stripe.utils'

import { snakeCaseToCamelCase } from '@elo-kit/utils/nameStyle.utils'
import { DEFAULT_STRIPE_AMOUNT, SETUP_FUTURE_USAGE, STRIPE_ELEMENTS_MODE } from 'constants/stripe.constants'

/* setupIntentPossible comes camelCased from the API, payment forms are snake_cased (e.g. google_pay -> googlePay) */
export const isSetupIntentPossible = (
  setupIntentPossible: Record<string, boolean> | undefined,
  paymentForm: string
): boolean => !!setupIntentPossible?.[snakeCaseToCamelCase(paymentForm)]

export interface ExpressPayOptions {
  amount: number
  currency: string
}

export interface ExpressCheckoutOrderResponse {
  clientSecret?: string | null
  setupIntentSecret?: string | null
  redirectLink?: string
}

/*
  Elements `mode` is fixed at creation time and cannot be changed via elements.update().
  When the order can be handled with a SetupIntent (nothing to charge today — test period,
  custom start day, free first rate) the express element must be created in `setup` mode
  without an amount; otherwise in `payment` mode with the amount.
*/
export const getExpressElementsBaseOptions = (
  withSetupIntent: boolean,
  payOptions: ExpressPayOptions
): StripeElementsOptions => ({
  locale: getStripeLocale(),
  currency: payOptions.currency,
  setupFutureUsage: SETUP_FUTURE_USAGE.offSession,
  ...(withSetupIntent
    ? { mode: STRIPE_ELEMENTS_MODE.setup }
    : { mode: STRIPE_ELEMENTS_MODE.payment, amount: payOptions.amount || DEFAULT_STRIPE_AMOUNT }),
})

export const getExpressElementsUpdateOptions = (withSetupIntent: boolean, payOptions: ExpressPayOptions) =>
  withSetupIntent
    ? { currency: payOptions.currency }
    : { amount: payOptions.amount || DEFAULT_STRIPE_AMOUNT, currency: payOptions.currency }

/*
  Confirms the intent created for an express (Google Pay / Apple Pay) order.
  The backend returns `setupIntentSecret` when the order needs a SetupIntent
  and `clientSecret` when it needs a PaymentIntent. When both are present
  (e.g. a debt charge response echoing the original, already succeeded
  SetupIntent) the PaymentIntent is the one to confirm. On success Stripe
  redirects to `redirectLink`; on failure `onError` is called with the error.
*/
const getGenericError = (): StripeError => ({ message: I18n.t('react.shared.notify.error') }) as StripeError

export const confirmExpressCheckoutOrder = async (
  stripeClient: Stripe,
  elements: StripeElements,
  resp: ExpressCheckoutOrderResponse | void,
  onError: (error: StripeError) => void
): Promise<void> => {
  if (!resp) {
    onError(getGenericError())
    return
  }

  /* Nothing to confirm client-side — the backend finished the payment, just land on the return page */
  if (!resp.clientSecret && !resp.setupIntentSecret) {
    if (resp.redirectLink) {
      window.location.href = resp.redirectLink
    } else {
      onError(getGenericError())
    }

    return
  }

  if (!stripeClient || !elements) {
    onError(getGenericError())
    return
  }

  const confirmParams = {
    return_url: resp.redirectLink || window.location.href,
  }

  if (resp.clientSecret) {
    const { error } = await stripeClient.confirmPayment({
      elements,
      clientSecret: resp.clientSecret,
      confirmParams,
    })

    if (error) {
      if (error.payment_intent?.status === 'succeeded') {
        if (resp.redirectLink) {
          window.location.href = resp.redirectLink
        }

        return
      }

      onError(error)
    }
  } else if (resp.setupIntentSecret) {
    const { error } = await stripeClient.confirmSetup({
      elements,
      clientSecret: resp.setupIntentSecret,
      confirmParams,
    })

    if (error) {
      if (error.setup_intent?.status === 'succeeded') {
        if (resp.redirectLink) {
          window.location.href = resp.redirectLink
        }

        return
      }

      onError(error)
    }
  }
}
